import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { Pagination } from '@/components/molecules';
import { useGRNsList, useWarehousesList } from '@/hooks/useErpApi';

interface GRNListProps {
  onCreateClick?: () => void;
  filters?: {
    purchaseOrderId?: string;
    warehouseId?: string;
    status?: 'accepted' | 'rejected' | 'partial';
  };
}

export const GRNList: React.FC<GRNListProps> = ({ onCreateClick, filters: externalFilters }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const { data: grns, isLoading, error, refetch } = useGRNsList({
    limit: itemsPerPage,
    offset,
    ...(selectedStatus !== 'all' && { status: selectedStatus }),
    ...(selectedWarehouse !== 'all' && { warehouse_id: selectedWarehouse }),
  });
  const { data: warehouses } = useWarehousesList();

  const statuses = ['all', 'accepted', 'rejected', 'partial'];

  // Client-side filtering for search
  const filteredGRNs = useMemo(() => {
    return (grns || []).filter((grn) => {
      const matchesSearch =
        grn.grn_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        grn.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        grn.warehouse_name.toLowerCase().includes(searchQuery.toLowerCase());

      // Apply external filters if provided
      if (externalFilters?.purchaseOrderId && grn.po_id !== externalFilters.purchaseOrderId) {
        return false;
      }
      if (externalFilters?.warehouseId && grn.warehouse_id !== externalFilters.warehouseId) {
        return false;
      }
      if (externalFilters?.status && grn.quality_status !== externalFilters.status) {
        return false;
      }

      return matchesSearch;
    });
  }, [grns, searchQuery, externalFilters]);

  // Get total count from API response if available, otherwise use filtered length
  const totalGRNs = (grns as any)?.total_count || filteredGRNs.length;
  
  // Reset to page 1 when search or filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };
  
  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };
  
  const handleWarehouseChange = (warehouse: string) => {
    setSelectedWarehouse(warehouse);
    setCurrentPage(1);
  };

  const getStatusBadge = (status: 'accepted' | 'rejected' | 'partial') => {
    switch (status) {
      case 'accepted':
        return <Badge variant="success">Accepted</Badge>;
      case 'rejected':
        return <Badge variant="error">Rejected</Badge>;
      case 'partial':
        return <Badge variant="warning">Partial</Badge>;
    }
  };

  const handleRowClick = (id: string) => {
    navigate(`/grns/${id}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading GRNs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="CircleAlert" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">Failed to Load GRNs</h3>
              <p className="text-error-700 mb-3">Unable to fetch GRN data. Please try again.</p>
              <Button variant="primary" size="sm" onClick={() => refetch()}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!externalFilters && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">Goods Receipt Notes</h1>
            <p className="text-neutral-600 mt-2">Manage inventory receipts from purchase orders</p>
          </div>
          {onCreateClick && (
            <Button variant="primary" className="sm:w-auto" onClick={onCreateClick}>
              <Icon name="Plus" size={20} className="mr-2" />
              Create GRN
            </Button>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Icon
                name="Search"
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400"
              />
              <Input
                type="text"
                placeholder="Search by GRN number, PO number, or warehouse..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <div className="flex gap-2">
            <span className="text-sm font-medium text-neutral-700 self-center">Status:</span>
            {statuses.map((status) => (
              <button
                key={status}
                onClick={() => handleStatusChange(status)}
                className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                  selectedStatus === status
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {warehouses && warehouses.length > 0 && (
            <div className="flex gap-2 ml-4">
              <span className="text-sm font-medium text-neutral-700 self-center">Warehouse:</span>
              <select
                value={selectedWarehouse}
                onChange={(e) => handleWarehouseChange(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Warehouses</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {filteredGRNs.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileText" size={48} className="text-neutral-300 mx-auto mb-4" />
            <p className="text-lg font-medium text-neutral-600">No GRNs found</p>
            <p className="text-neutral-500 mt-2">
              {searchQuery || selectedStatus !== 'all' || selectedWarehouse !== 'all'
                ? 'Try adjusting your search criteria'
                : 'Create your first GRN to get started'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      GRN Number
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      PO Number
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Warehouse
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Received Date
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Items
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGRNs.map((grn) => (
                    <tr
                      key={grn.id}
                      className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(grn.id)}
                    >
                      <td className="py-3 px-4">
                        <span className="font-medium text-neutral-900">{grn.grn_number}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-neutral-700">{grn.po_number}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-neutral-700">{grn.warehouse_name}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-neutral-700">{formatDate(grn.received_date)}</span>
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(grn.quality_status as 'accepted' | 'rejected' | 'partial')}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-neutral-700">{grn.items.length} items</span>
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(grn.id);
                          }}
                        >
                          <Icon name="Eye" size={16} className="mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={totalGRNs}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              disabled={isLoading}
            />
          </>
        )}
      </div>
    </div>
  );
};
